/* TODO: Issue MRP-2775 */
/* eslint-disable max-lines */
import { Box, Link as CLink, Flex, IconButton, Show, StackDivider, useOutsideClick, VStack } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, Route, Switch, useLocation } from "react-router-dom";

import { EditIcon, LinkIcon, SettingsIcon } from "@chakra-ui/icons";

import { Avatar, Cart, Close, Handshake, Home, MRLogo, Pricing, Shirt, Store, Tag, Shopify } from "ui-kit/icon";

import { Icon, Text } from "ui-kit";

import { IIngestionSourceData } from "constants/ingestion/types";
import { getIsLoggedIn } from "store/selectors/auth";
import {
  getUser,
  getUserData,
  getUserNames,
  getUserRetailerIngestionSource,
  getUserRetailerIsShellAccount,
  getUserSellerType,
  getUserRetailerId,
  getUserOrderForwardingSettings,
} from "store/selectors/user";

import { LogoNoSwap as logo } from "static/images";
import { SHOW_NEW_FEATURE_PANEL } from "components/layout/constants";
import HeaderImpersonationBox from "components/layout/header/header-impersonation-box/header-impersonation-box";
import NewFeaturePanel from "components/layout/side-nav/new-feature-panel";
import { SideNavAreaName } from "components/layout/side-nav/side-nav-area-name";
import { SideNavCartLink, SideNavLink } from "components/layout/side-nav/side-nav-link";
import { FEATURE_FLAG_MARKDOWN_CADENCE, FEATURE_FLAG_ERROR_REPORT } from "constants/feature-flags";
import { ApiFeatureFlagged, useIsSearchV2Enabled, useQueryFeatureFlag } from "hooks/feature-flags";
import { useErrorReport } from "hooks/error-report";
import { getActiveVacation } from "store/selectors/settings";

export const SideNavLinkList = props => (
  <Flex as="ul" direction="column" gap="1" listStyleType="none" w="full" {...props} />
);

const ListInventoryNavButton = ({ ingestionSource }: { ingestionSource: IIngestionSourceData }) => {
  const isShellAccount = useSelector(getUserRetailerIsShellAccount);
  const activeVacation = useSelector(getActiveVacation);
  const isOnVacation = activeVacation?.isActive;
  const user = useSelector(getUser);
  const isManualSeller = user?.data?.integrations?.some(integration => integration.name === "MR_GENERIC");
  const shouldDisableListInventory = isManualSeller && isOnVacation;

  const linkData = ingestionSource.isEcom
    ? {
        href: ingestionSource.listInventory.linkHref,
        external: true,
        isExternal: true
      }
    : {
        to: ingestionSource.listInventory.linkHref,
        external: false
      };

  return (
    <SideNavLink
      icon={ingestionSource.listInventory.icon}
      isLocked={isShellAccount || shouldDisableListInventory}
      tooltipDescription={
        shouldDisableListInventory
          ? "To list inventory, go to Settings and Reactivate your account"
          : "List your items for sale on Max Retail"
      }
      _hover={{
        backgroundColor: isShellAccount || shouldDisableListInventory ? "gray.200" : "gray.200"
      }}
      {...linkData}
      data-testid="side-nav-list-inventory-link"
    >
      List Inventory
    </SideNavLink>
  );
};

const SideNav = ({ onClose = () => {} }) => {
  const location = useLocation();
  const ref = useRef(null);
  const ingestionSource = useSelector(getUserRetailerIngestionSource);
  const { orderForwardingEnabled } = useSelector(getUserOrderForwardingSettings);
  const { first_name } = useSelector(getUserNames);
  const user = useSelector(getUserData);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isShellAccount = useSelector(getUserRetailerIsShellAccount);
  const getSellerType = useSelector(getUserSellerType);
  const isBrandSeller = getSellerType === "brand";
  const isSearchV2Enabled = useIsSearchV2Enabled();

  const retailerId = useSelector(getUserRetailerId);
  const { isFeatureFlagEnabled: isErrorReportEnabled } = useQueryFeatureFlag(FEATURE_FLAG_ERROR_REPORT);
  const { data: errorData } = useErrorReport({
    retailerId,
    page: 1,
    limit: 1
  });
  const hasErrors = isErrorReportEnabled && errorData?.error && errorData.error.length > 0;

  useEffect(() => onClose(), [location.key]);

  useOutsideClick({
    ref,
    handler: () => onClose()
  });

  return (
    <Box ref={ref} as="nav" bg="white" flexBasis="300px" flexShrink={0} minH="100vh" zIndex="overlay">
      <Flex direction="column" h="100vh">
        <Box flexGrow={1} px="15px" pt="27px" mb="auto">
          <Show below="lg">
            <IconButton aria-label="close-button-side-nav" onClick={onClose} fontSize="xl" mb="4" variant="unstyled">
              <Icon as={Close} boxSize="10" />
            </IconButton>
          </Show>

          <Show above="lg">
            <Box pl="3" mb="6">
              <Link to="/">
                <img src={logo} alt="Max Retail logo" className="header__logo__img" />
              </Link>
            </Box>
          </Show>

          <Switch>
            <Route path="/admin*">
              <VStack alignItems="flex-start" gap="2" divider={<StackDivider borderColor="gray.100" />}>
                <SideNavAreaName>
                  <CLink as={Link} to="/">
                    &laquo; Back
                  </CLink>
                </SideNavAreaName>
                <SideNavLinkList>
                  <SideNavLink icon={MRLogo} exact to="/admin">
                    Admin
                  </SideNavLink>
                  <SideNavLink icon={EditIcon} exact to="/admin/verifications">
                    Verifications
                  </SideNavLink>
                  <ApiFeatureFlagged featureName="retailers.pim.show-admin-link">
                    <SideNavLink icon={EditIcon} exact to="/admin/pim">
                      PIM - Image management
                    </SideNavLink>
                    <SideNavLink to="/search" icon={Cart}>
                      Max Search 2.0
                    </SideNavLink>
                  </ApiFeatureFlagged>
                  <SideNavLink icon={LinkIcon} exact to="/admin/matching">
                    Matching
                  </SideNavLink>
                  <SideNavLink icon={LinkIcon} exact to="/admin/image-verification">
                    ImageVerification
                  </SideNavLink>
                  <SideNavLink icon={Tag} exact to="/admin/catalog">
                    Product Catalog
                  </SideNavLink>
                  <SideNavLink icon={Tag} exact to="/admin/rtr">
                    RTR Upload
                  </SideNavLink>
                  <SideNavLink icon={Tag} exact to="/admin/csv">
                    CSV Upload
                  </SideNavLink>
                </SideNavLinkList>

                <HeaderImpersonationBox showLinks={false} />
              </VStack>
            </Route>
            <Route path="/:path*">
              <VStack alignItems="flex-start" gap="2" divider={<StackDivider borderColor="gray.100" />}>
                <SideNavLinkList gap="3">
                  <SideNavAreaName>Menu</SideNavAreaName>
                  <Box as="li" listStyleType="none">
                    <SideNavLinkList>
                      <SideNavLink
                        icon={Home}
                        to="/"
                        isActive={(_, currentUrl) =>
                          currentUrl.pathname === "/" && !currentUrl.search.includes("tab=requests")
                        }
                        data-testid="side-nav-home-link"
                      >
                        Home
                      </SideNavLink>
                      {!isShellAccount && (
                        <SideNavLink
                          icon={orderForwardingEnabled ? Shopify : Store}
                          to="/orders?from=top_menu"
                          isActive={(match, loc) => loc.pathname.startsWith("/orders")}
                          data-testid="side-nav-orders-link"
                        >
                          My Orders
                        </SideNavLink>
                      )}
                      <SideNavLink
                        isLocked={isShellAccount}
                        tooltipDescription="See all your items listed for sale on Max Retail"
                        to="/mylistings"
                        exact
                        icon={Shirt}
                        _hover={{
                          backgroundColor: isShellAccount ? "gray.200" : "gray.200"
                        }}
                        data-testid="side-nav-my-inventory-link"
                      >
                        <Flex align="center" gap="2">
                          My Inventory
                          {hasErrors && (
                            <Box
                              ml="2"
                              boxSize="18px"
                              bg="red.500"
                              color="white"
                              borderRadius="full"
                              fontSize="xs"
                              fontWeight="bold"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              !
                            </Box>
                          )}
                        </Flex>
                      </SideNavLink>
                      <ApiFeatureFlagged featureName={FEATURE_FLAG_MARKDOWN_CADENCE}>
                        <SideNavLink to="/pricing" exact icon={Pricing} data-testid="side-nav-price-drops-link">
                          Price Drops
                        </SideNavLink>
                      </ApiFeatureFlagged>
                      <ListInventoryNavButton ingestionSource={ingestionSource} />
                      {!isShellAccount && !isBrandSeller && (
                        <SideNavLink
                          to={isSearchV2Enabled ? "/search" : "/browse"}
                          exact
                          icon={Cart}
                          data-testid="side-nav-marketplace-link"
                        >
                          Shop Marketplace
                        </SideNavLink>
                      )}
                      <SideNavLink to="/refer-friend/" icon={Handshake} data-testid="side-nav-refer-friend-link">
                        Refer a Friend
                      </SideNavLink>
                      <SideNavCartLink />
                    </SideNavLinkList>
                  </Box>
                </SideNavLinkList>

                <SideNavLinkList gap="3">
                  <Box as="li" listStyleType="none">
                    <SideNavLinkList>
                      {!isShellAccount && (
                        <SideNavLink to="/requestListing" exact strict data-testid="side-nav-request-listing-link">
                          Request What You Need
                        </SideNavLink>
                      )}
                      {!isBrandSeller && (
                        <SideNavLink
                          to="/?tab=requests"
                          isActive={(_, currentUrl) =>
                            currentUrl.pathname !== "/mylistings" && currentUrl.search.includes("tab=requests")
                          }
                          isLocked={isShellAccount}
                          tooltipDescription="View items requested by other sellers"
                          _hover={{
                            backgroundColor: isShellAccount ? "gray.200" : "gray.200"
                          }}
                          data-testid="side-nav-view-requests-link"
                        >
                          View Seller Requests
                        </SideNavLink>
                      )}
                      {!isBrandSeller && (
                        <SideNavLink
                          isLocked={isShellAccount}
                          tooltipDescription="View the brands your store carries"
                          to="/brands"
                          _hover={{
                            backgroundColor: isShellAccount ? "gray.200" : "gray.200"
                          }}
                          data-testid="side-nav-my-brands-link"
                        >
                          My Brands
                        </SideNavLink>
                      )}
                    </SideNavLinkList>
                  </Box>
                </SideNavLinkList>

                <HeaderImpersonationBox />
              </VStack>
            </Route>
          </Switch>
        </Box>

        {isLoggedIn && (
          <Flex
            as={Link}
            to="/knowledge-center/"
            target="_blank"
            d="inline-flex"
            flexGrow={0}
            h="40px"
            p="2"
            marginLeft="4"
            gap="4"
            data-testid="side-nav-knowledge-center-link"
          >
            <Box color="blue.darkest">
              <Text textStyle="body" mb="0" lineHeight="1.3" fontSize="14px" opacity=".6">
                Knowledge Center
              </Text>
            </Box>
          </Flex>
        )}

        <Flex
          as={Link}
          to="/terms/"
          d="inline-flex"
          flexGrow={0}
          h="40px"
          p="2"
          marginLeft="4"
          data-testid="side-nav-terms-link"
        >
          <Box color="blue.darkest">
            <Text textStyle="body" mb="0" lineHeight="1.3" fontSize="14px" opacity=".6">
              Terms and Conditions
            </Text>
          </Box>
        </Flex>

        <Flex
          as={Link}
          to="/privacy-policy/"
          d="inline-flex"
          flexGrow={0}
          h="40px"
          p="2"
          marginBottom="4"
          marginLeft="4"
          gap="4"
          data-testid="side-nav-privacy-policy-link"
        >
          <Box color="blue.darkest">
            <Text textStyle="body" mb="0" lineHeight="1.3" fontSize="14px" opacity=".6">
              Privacy Policy
            </Text>
          </Box>
        </Flex>

        <Box bgColor="white" sx={{ position: "sticky", bottom: "0" }}>
          {SHOW_NEW_FEATURE_PANEL && <NewFeaturePanel />}

          <Flex
            as={Link}
            to="/settings"
            d="inline-flex"
            flexGrow={0}
            w="full"
            h="80px"
            p="5"
            gap="4"
            borderTop={SHOW_NEW_FEATURE_PANEL ? "1px solid #E4E8EA" : undefined}
            shadow={SHOW_NEW_FEATURE_PANEL ? undefined : "0 -3pt 6pt hsla(0, 0%, 0%, 0.1)"}
          >
            <Flex justify="center" align="center" borderRadius="md" bg="blue.light" boxSize="10">
              <Icon as={SettingsIcon} boxSize="1.75rem" />
            </Flex>

            <Box color="blue.darkest">
              <Text textStyle="body" mb="0" lineHeight="1.3">
                {first_name ?? "N/A"}
              </Text>
              <Text opacity="0.5" fontSize="xs" textStyle="body4" lineHeight="1">
                {user.email}
              </Text>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default SideNav;
